import { Form, Input, message, Skeleton, Spin } from "antd";
import { useCallback, useEffect, useState } from "react";

import { AssistitoRepository, AuthRepository, GeneralRepository } from "core/data";
import { IRegisteredUserData } from "core/interfaces/data/IRegisterRequest";
import IChangePasswordProps from "components/ChangePassword/IChangePasswordProps";
import i18n from "utils/i18n";
import { validateCf, validatePassword } from "utils/Validate";
import "./ChangePassword.css";
import { CustomButtonType } from "components/CustomButton/CustomButtonType";
import { useErrorHandler } from "react-error-boundary";
import { performAsyncFunction } from "utils/AsyncFunctionUtility";
import { StorageHelper } from "utils/SessionStorageHelper";
import Label from "components/Label/Label";
import CustomButton from "components/CustomButton/CustomButton";
import { customNotification } from "components/CustomNotification/CustomNotification";
import PasswordForm from "components/PasswordForm/PasswordForm";
import { Enhanced } from "utils/ReactUtils";

const ChangePassword: React.FC<IChangePasswordProps> = ({ OnSubmit, OnChangePasswordEnd, ChangePasswordRequired,t,sessionInfo }) => {
	
	const [form] = Form.useForm();
	const [loading, setLoading] = useState<boolean>(true);
	const [saving, setSaving] = useState<boolean>(false);
	const [registeredUser, setRegisteredUser] = useState<IRegisteredUserData | undefined>(undefined);
	const [passwordMinLenght, setPasswordMinLenght] = useState<string>("");
	const handleError = useErrorHandler();


	const changePasswordSubmitBtn = sessionInfo.isLogged ? t("changePassword.submitChange") : t("resetPassword.submitReset");

	const GetUserData = useCallback(async () => {
		var loginData = await AssistitoRepository.GetLoggedUserData({
			IdSessione: sessionInfo.sessione?.idSessione,
			culture: i18n.language,
		});
		return loginData;
	}, [sessionInfo.sessione?.idSessione]);

	useEffect(() => {
		performAsyncFunction(async () => {
			setLoading(true);
			if (sessionInfo.isLogged) {
				// recupero i dati dell'utente
				const user = await GetUserData();
				setRegisteredUser(user);
			}
			setLoading(false);
			if (!passwordMinLenght || passwordMinLenght === "") {
				let minLenght = await GeneralRepository.passwordMinLenghtGet();
				setPasswordMinLenght(minLenght);
			}
		}, handleError);
	}, [GetUserData, handleError, passwordMinLenght, sessionInfo.isLogged]);

	const OnFinishFailed = (e: any) => {
		const input = document.querySelector(`#${e.errorFields[0]?.name[0]}`);

		if (input) {
			input.scrollIntoView({
				behavior: "smooth",
				block: "start",
				inline: "start",
			});
		}
	};

	const OnSubmitForm = async (data: any) => {
		let changePasswordSuccesso = false;
		let failErrorMessage = t("changePassword.errorMessagges.generic");
		try {
			if (sessionInfo.isLogged || StorageHelper.hasItem("resetPasswordToken")) {
				let oldPassword = data.OldPassword;

				if (oldPassword! !== data.Password! || ChangePasswordRequired) {
					setSaving(true);

					let changePasswordRes = await AuthRepository.ChangePassword(
						{
							OldPassword: oldPassword,
							NewPassword: data.Password!,
							culture: i18n.language,
						},
						StorageHelper.getItem("resetPasswordToken")
					);

					changePasswordSuccesso = changePasswordRes.esito;
					if (changePasswordSuccesso) {
						OnChangePasswordEnd();
					} else if (changePasswordRes.message) {
						failErrorMessage = changePasswordRes.message;
						message.error(failErrorMessage, 20);
					}
					setSaving(false);
				} else {
					failErrorMessage = t("changePassword.errorMessagges.samePassword");
				}
			} else {
				setSaving(true);

				await AuthRepository.ResetPassword({
					Username: data.Username!,
					CodiceFiscale: data.CodiceFiscale!,
					culture: i18n.language,
				});
				OnSubmit();
				customNotification(t("changePassword.generic.title"), t("changePassword.generic.message"), "info", undefined, "top");
				setSaving(false);
			}
		} catch (error: any) {
			message.error(failErrorMessage, 25);
		}
	};

	const validatePasswordvsMail = async (rule: any, value: any, callback: any, text: string) => {
		if (sessionInfo.isLogged) {
			if (registeredUser?.Email === value) await Promise.reject(text);
		}

		await Promise.resolve(value);
	};

	return (
		<>
			<div id="changePassword">
				{
					loading ? (
						<Skeleton active />
					) : (
						<Spin size="large" spinning={saving}>
							<>
								<Form onFinishFailed={OnFinishFailed} onFinish={OnSubmitForm} form={form}>
									{sessionInfo.isLogged || StorageHelper.hasItem("resetPasswordToken") ? (
										<>
											<div className="title item">
												<span>{t("changePassword.title")}</span>
											</div>

											{!StorageHelper.hasItem("resetPasswordToken") && (
												<div>
													<Label text={t("changePassword.form.oldPassword")} />
													<div>
														<Form.Item
															name="OldPassword"
															rules={[
																{
																	required: true,
																	message: t("login.messagges.password"),
																},
																{
																	validator: (rule: any, value: any, callback: any) =>
																		validatePassword(
																			rule,
																			value,
																			callback,
																			t("register.validationMessages.password").replace("<%NumeroCaratteri%>", passwordMinLenght)
																		),
																},
																{
																	validator: (rule: any, value: any, callback: any) =>
																		validatePasswordvsMail(rule, value, callback, t("register.validationMessages.PasswordvsMail")),
																},
															]}
														>
															<Input.Password size="middle" />
														</Form.Item>
													</div>
												</div>
											)}
											<PasswordForm mail={registeredUser?.Email} oldPassword={form.getFieldValue("OldPassword")} />
										</>
									) : (
										<>
											<div>
												<div className="title item">
													<span>{t("resetPassword.title")}</span>
												</div>
												<Label text={t("resetPassword.username.label")} />
												<div>
													<Form.Item
														name="Username"
														rules={[
															{
																required: true,
																message: t("resetPassword.requiredMessages.username"),
															},
														]}
													>
														<Input type="text" size="large" placeholder={t("resetPassword.username.placeholder")} />
													</Form.Item>
												</div>
												<Label text={t("resetPassword.cf.label")} />
												<div>
													<Form.Item
														name="CodiceFiscale"
														normalize={(props) => props.toUpperCase()}
														rules={[
															{
																required: true,
																message: t("resetPassword.requiredMessages.cf"),
															},
															{
																validator: (rule: any, value: any, callback: any) =>
																	validateCf(rule, value, callback, t("resetPassword.validationMessages.cf")),
															},
														]}
													>
														<Input maxLength={16} placeholder={t("resetPassword.cf.placeholder")} size="large" />
													</Form.Item>
												</div>
											</div>
										</>
									)}
									<div className="buttonContainer">
										<CustomButton
											buttonType={CustomButtonType.negative}
											text={t("changePassword.cancel")}
											style={{
												display: ChangePasswordRequired || StorageHelper.hasItem("resetPasswordToken") ? "none" : "",
											}}
											OnClick={OnChangePasswordEnd}
										/>
										<CustomButton text={changePasswordSubmitBtn} htmlType="submit" buttonType={CustomButtonType.positive} />
									</div>
								</Form>
							</>
						</Spin>
					)

					// <Alert message={t('errorMessages.base')} type='error' showIcon banner />
				}
			</div>
		</>
	);
};

export default Enhanced( ChangePassword,{componentNamespace:"ChangePassword"});
